import { useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import { useDispatch, useSelector } from 'react-redux';
import {
    Table, TableBody, Typography, TableContainer, TableHead, TableRow, Paper,
    IconButton, Pagination, Stack, Box, Tooltip, Dialog, DialogTitle,
    DialogContent, DialogContentText, DialogActions, Button, Switch, TextField,
    FormControlLabel, Checkbox
} from '@mui/material';
import LockResetIcon from '@mui/icons-material/LockReset';
import EmailIcon from '@mui/icons-material/Email';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PasswordIcon from '@mui/icons-material/Password';
import { toast } from 'react-toastify';
import {
    getPatientsRequest,
    resetPasswordRequest,
    sendResetEmailRequest,
    clearResetPassword,
    clearSendResetEmail,
    toggleImageAccessRequest,
    clearToggleImageAccess
} from '../../../redux/modules/patients/slice';
import SearchInput from '../../../utils/searchInput';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}));

const EllipsisText = ({ children }) => (
    <Typography noWrap>
        {children}
    </Typography>
);

const tableHeading = ["Name", "Login (Email/Username)", "Date of Birth", "Welcome Email", "View Images", "Actions"];
const limit = 10;

export default function PatientAccountsList() {
    const dispatch = useDispatch();
    const { getPatientsRes, resetPasswordRes, sendResetEmailRes, toggleImageAccessRes, loading } = useSelector((state) => state.patients);

    const [currentPage, setCurrentPage] = useState(1);
    const [searchQuery, setSearchQuery] = useState("");
    const [passwordDialog, setPasswordDialog] = useState({ open: false, password: null, patientName: '' });
    const [confirmDialog, setConfirmDialog] = useState({ open: false, type: null, patientId: null, patientName: '', hasEmail: true });
    const [manualPassword, setManualPassword] = useState('');
    const [useManualPassword, setUseManualPassword] = useState(false);

    useEffect(() => {
        dispatch(getPatientsRequest({ page: currentPage, limit, searchQuery }));
    }, [dispatch, currentPage, searchQuery]);

    // Handle reset password response
    useEffect(() => {
        if (!resetPasswordRes) return;
        if (resetPasswordRes?.success) {
            if (resetPasswordRes.newPassword) {
                // Show generated password
                setPasswordDialog({
                    open: true,
                    password: resetPasswordRes.newPassword,
                    patientName: confirmDialog.patientName
                });
            } else {
                // Manual password was set
                toast.success('Password set successfully');
            }
        } else {
            toast.error(resetPasswordRes?.message || 'Failed to reset password');
        }
        dispatch(clearResetPassword());
        handleCloseConfirmDialog();
    }, [resetPasswordRes, dispatch, confirmDialog.patientName]);

    // Handle send reset email response
    useEffect(() => {
        if (!sendResetEmailRes) return;
        if (sendResetEmailRes?.success) {
            toast.success('Password reset email sent successfully');
        } else {
            toast.error(sendResetEmailRes?.message || 'Failed to send reset email');
        }
        dispatch(clearSendResetEmail());
        handleCloseConfirmDialog();
    }, [sendResetEmailRes, dispatch]);

    // Handle toggle image access response
    useEffect(() => {
        if (!toggleImageAccessRes) return;
        if (toggleImageAccessRes?.success) {
            toast.success(toggleImageAccessRes.message || 'Image access updated');
        } else {
            toast.error(toggleImageAccessRes?.message || 'Failed to update image access');
        }
        dispatch(clearToggleImageAccess());
    }, [toggleImageAccessRes, dispatch]);

    const handleToggleImageAccess = (patient, checked) => {
        dispatch(toggleImageAccessRequest({ patientId: patient._id, canViewImages: checked }));
    };

    const handleResetPassword = (patient) => {
        setManualPassword('');
        setUseManualPassword(false);
        setConfirmDialog({
            open: true,
            type: 'reset',
            patientId: patient._id,
            patientName: `${patient.firstName} ${patient.lastName || ''}`.trim(),
            hasEmail: !!patient.email
        });
    };

    const handleSendResetEmail = (patient) => {
        setConfirmDialog({
            open: true,
            type: 'email',
            patientId: patient._id,
            patientName: `${patient.firstName} ${patient.lastName || ''}`.trim(),
            hasEmail: !!patient.email
        });
    };

    const handleConfirmAction = () => {
        if (confirmDialog.type === 'reset') {
            if (useManualPassword && manualPassword) {
                dispatch(resetPasswordRequest({ patientId: confirmDialog.patientId, manualPassword }));
            } else {
                dispatch(resetPasswordRequest(confirmDialog.patientId));
            }
        } else if (confirmDialog.type === 'email') {
            dispatch(sendResetEmailRequest(confirmDialog.patientId));
        }
    };

    const handleCloseConfirmDialog = () => {
        setConfirmDialog({ open: false, type: null, patientId: null, patientName: '', hasEmail: true });
        setManualPassword('');
        setUseManualPassword(false);
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast.success('Password copied to clipboard');
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        // Use UTC to avoid timezone shift issues
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric' });
    };

    let pageCount = 0;
    if (getPatientsRes?.dataCount && getPatientsRes?.dataCount > limit) {
        pageCount = Math.ceil(getPatientsRes?.dataCount / limit);
    }

    return (
        <>
            <Stack mb={3} direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between">
                <SearchInput setSearchQuery={(searchValue) => { setSearchQuery(searchValue); setCurrentPage(1); }} />
            </Stack>

            <Box sx={{ overflowX: { xs: 'auto', md: 'visible' }, mx: { xs: -2, sm: 0 }, px: { xs: 2, sm: 0 } }}>
                <TableContainer component={Paper} className='tableShadow'>
                    <Table sx={{ minWidth: { xs: 500, md: 700 } }} aria-label="patient accounts table" className='commonTable'>
                        <TableHead>
                            <TableRow>
                                {tableHeading.map(elem => (<StyledTableCell key={elem}>{elem}</StyledTableCell>))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {getPatientsRes?.data?.length > 0 ?
                                getPatientsRes?.data?.map(patient => (
                                    <StyledTableRow key={patient._id}>
                                        <StyledTableCell align="left">
                                            <EllipsisText>{`${patient.firstName} ${patient.lastName || ''}`.trim()}</EllipsisText>
                                        </StyledTableCell>
                                        <StyledTableCell align="left">
                                            <EllipsisText>
                                                {patient.email || patient.username || 'N/A'}
                                                {!patient.email && patient.username && (
                                                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                                        (username)
                                                    </Typography>
                                                )}
                                            </EllipsisText>
                                        </StyledTableCell>
                                        <StyledTableCell align="left">
                                            <EllipsisText>{formatDate(patient.dob)}</EllipsisText>
                                        </StyledTableCell>
                                        <StyledTableCell align="left">
                                            <Typography
                                                sx={{
                                                    color: !patient.email ? 'text.disabled' : patient.welcomeEmailSent ? 'success.main' : 'warning.main',
                                                    fontWeight: 500
                                                }}
                                            >
                                                {!patient.email ? 'No Email' : patient.welcomeEmailSent ? 'Sent' : 'Pending'}
                                            </Typography>
                                        </StyledTableCell>
                                        <StyledTableCell align="left">
                                            <Tooltip title={patient.canViewImages ? "Click to disable image viewing" : "Click to enable image viewing"}>
                                                <Switch
                                                    checked={patient.canViewImages || false}
                                                    onChange={(e) => handleToggleImageAccess(patient, e.target.checked)}
                                                    disabled={loading}
                                                    color="primary"
                                                    size="small"
                                                />
                                            </Tooltip>
                                        </StyledTableCell>
                                        <StyledTableCell align="left">
                                            <Tooltip title="Set/Reset Password">
                                                <IconButton
                                                    color="primary"
                                                    onClick={() => handleResetPassword(patient)}
                                                    disabled={loading}
                                                >
                                                    <LockResetIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title={patient.email ? "Send Reset Email" : "No email address"}>
                                                <span>
                                                    <IconButton
                                                        color="info"
                                                        onClick={() => handleSendResetEmail(patient)}
                                                        disabled={loading || !patient.email}
                                                    >
                                                        <EmailIcon />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </StyledTableCell>
                                    </StyledTableRow>
                                ))
                                :
                                <StyledTableRow>
                                    <StyledTableCell colSpan={tableHeading.length} align="center">
                                        <Typography>No patient accounts found</Typography>
                                    </StyledTableCell>
                                </StyledTableRow>
                            }
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            {(getPatientsRes?.dataCount > limit) && (
                <Pagination
                    count={pageCount}
                    page={currentPage}
                    onChange={(event, value) => { setCurrentPage(value) }}
                    sx={{ mt: 2 }}
                />
            )}

            {/* Confirmation Dialog */}
            <Dialog open={confirmDialog.open} onClose={handleCloseConfirmDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {confirmDialog.type === 'reset' ? 'Set/Reset Password' : 'Send Reset Email'}
                </DialogTitle>
                <DialogContent>
                    {confirmDialog.type === 'reset' ? (
                        <>
                            <DialogContentText sx={{ mb: 2 }}>
                                Set password for <strong>{confirmDialog.patientName}</strong>
                            </DialogContentText>

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={useManualPassword}
                                        onChange={(e) => setUseManualPassword(e.target.checked)}
                                    />
                                }
                                label="Set password manually"
                            />

                            {useManualPassword ? (
                                <TextField
                                    autoFocus
                                    margin="dense"
                                    label="Enter Password"
                                    type="text"
                                    fullWidth
                                    variant="outlined"
                                    value={manualPassword}
                                    onChange={(e) => setManualPassword(e.target.value)}
                                    helperText="Enter a password for the patient (minimum 6 characters)"
                                    sx={{ mt: 1 }}
                                />
                            ) : (
                                <DialogContentText sx={{ mt: 1, color: 'text.secondary', fontSize: '0.875rem' }}>
                                    A random password will be generated and displayed for you to share with the patient.
                                </DialogContentText>
                            )}
                        </>
                    ) : (
                        <DialogContentText>
                            Are you sure you want to send a password reset email to <strong>{confirmDialog.patientName}</strong>?
                        </DialogContentText>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseConfirmDialog}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmAction}
                        variant="contained"
                        disabled={loading || (useManualPassword && manualPassword.length < 6)}
                    >
                        {loading ? 'Processing...' : (useManualPassword ? 'Set Password' : 'Confirm')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Password Display Dialog */}
            <Dialog open={passwordDialog.open} onClose={() => setPasswordDialog({ open: false, password: null, patientName: '' })}>
                <DialogTitle>Password Reset Successful</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        New password for <strong>{passwordDialog.patientName}</strong>:
                    </DialogContentText>
                    <Box sx={{
                        mt: 2,
                        p: 2,
                        backgroundColor: 'grey.100',
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }}>
                        <Typography variant="h6" fontFamily="monospace">
                            {passwordDialog.password}
                        </Typography>
                        <IconButton onClick={() => copyToClipboard(passwordDialog.password)} size="small">
                            <ContentCopyIcon />
                        </IconButton>
                    </Box>
                    <DialogContentText sx={{ mt: 2, color: 'warning.main' }}>
                        Please save this password and share it with the patient securely.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPasswordDialog({ open: false, password: null, patientName: '' })} variant="contained">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
